import React, { ChangeEvent, useState, forwardRef, Ref, ReactNode, MouseEventHandler } from 'react'
import clsx from 'clsx'

import { SIZES, VIEWBOX_MAP, TEXT_MAP, POLYLINE_MAP, KIND, Alignment, CheckboxThemeKindMapping } from './constants'
import styles from './style.module.scss'
import { Span } from '../Typography'
import { noop } from '../utils/function'
import { Override, ShellOption } from '../Option'
import { isCallable } from '@afterpay/utils'
import { useGetSelectedTheme, useThemeClass } from '../Theme'

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type CheckboxSizes = SizesType[SizesKeys]

type CSSClasses = keyof typeof styles

type KindType = typeof KIND
type KindKeys = keyof KindType
export type CheckboxKind = KindType[KindKeys]

export interface CheckboxProps {
  defaultChecked?: boolean
  checked?: boolean
  disabled?: boolean
  id?: string
  kind?: CheckboxKind
  label?: ReactNode | (() => ReactNode)
  name?: string
  onClick?: MouseEventHandler<HTMLInputElement>
  onChange?: (event: ChangeEvent<HTMLInputElement>, revertOptimisticUpdate: () => void) => any
  reversed?: boolean
  size?: CheckboxSizes
  bold?: boolean
  tabIndex?: number
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  testNameSpace?: string
  stretch?: boolean
  alignItems?: Alignment
  error?: boolean
}

export const BaseCheckbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      defaultChecked = false,
      disabled = false,
      id = Math.random().toString(36),
      kind = KIND.DEFAULT,
      label = '',
      name = '',
      onChange = noop,
      reversed = false,
      size = SIZES.MEDIUM,
      bold = false,
      tabIndex = 0,
      testNameSpace = '',
      stretch = false,
      alignItems = Alignment.TOP,
      error = false,
      ...otherProps
    },
    ref: Ref<any>
  ) => {
    const themeClass = useThemeClass()
    const selectedTheme = useGetSelectedTheme()
    const [isChecked, setChecked] = useState(defaultChecked)

    const kindOverride = CheckboxThemeKindMapping[selectedTheme]?.[kind] ?? kind

    const labelClasses = clsx(
      styles[`size${size}` as CSSClasses],
      styles.label,
      disabled && styles.disabled,
      kindOverride === KIND.SELECT && styles.rounded,
      reversed && styles.reversed,
      stretch && styles.stretch,
      alignItems === Alignment.CENTER && styles.itemsCenter,
      error && styles.error,
      styles[themeClass]
    )

    const containerClasses = clsx(styles.container, styles[`${themeClass}`])

    const hasTestId = testNameSpace.length > 0

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setChecked(e.target.checked)
      onChange(e, () => {
        setChecked(!e.target.checked)
        e.target.checked = !e.target.checked
      })
    }

    if (disabled) {
      tabIndex = -1
    }

    const renderLabelText = (): ReactNode => {
      return (
        <label htmlFor={id} className={labelClasses} {...(hasTestId && { 'data-testid': `${testNameSpace}-label` })}>
          {kindOverride === KIND.TOGGLE ? (
            <span className={styles.slider} role='presentation'></span>
          ) : (
            <span className={styles.checkmark} role='presentation'>
              <svg viewBox={VIEWBOX_MAP[size]} className={styles.svg}>
                <polyline
                  className={styles.checkboxPath}
                  stroke='#fff'
                  strokeLinejoin='round'
                  strokeLinecap='round'
                  strokeWidth='2.5'
                  points={POLYLINE_MAP[size]}
                />
              </svg>
            </span>
          )}
          {label !== '' && (
            <span className={styles.text}>
              <Span size={TEXT_MAP[size]} bold={bold} {...(hasTestId && { 'data-testid': `${testNameSpace}-label-text` })}>
                {isCallable(label) ? label() : label}
              </Span>
            </span>
          )}
        </label>
      )
    }

    const renderInput = (): ReactNode => {
      return (
        <input
          {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })}
          type='checkbox'
          id={id}
          name={name}
          className={styles.checkbox}
          disabled={disabled}
          tabIndex={tabIndex}
          aria-checked={isChecked}
          onChange={handleChange}
          defaultChecked={defaultChecked}
          {...otherProps}
        />
      )
    }

    const Component = (
      <div className={containerClasses} ref={ref}>
        {renderInput()}
        {renderLabelText()}
      </div>
    )

    if (kindOverride === KIND.OPTION) {
      return <ShellOption component={Component} testNameSpace='checkbox' override={Override.DisabledBorderRadius} />
    }

    return Component
  }
)

export const Checkbox = BaseCheckbox

BaseCheckbox.displayName = 'Checkbox'
Checkbox.displayName = 'Checkbox'
